import { useNotify, useRefresh } from "react-admin";
import { useCallback } from "react";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { AlertType, ResourceName, StoplossClaim } from "@/types";
import { validateAuraSubmit } from "@/resources/stoploss-claims/auraSubmitValidation";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { apiErrorBody } from "@/utils/errors/apiErrorCode";

type SubmitTarget = "aura" | "carrier";

/**
 * Response of POST /stoploss-claims/submit-to-aura (Coronis→Aura sender, US #5330).
 * Top-level status: 200 when any group succeeded (failures may still be present —
 * partial), 422 when all failed on a 4xx, 502 when all failed on a 5xx.
 */
interface AuraSubmitFailure {
  stage?: string;
  code?: string;
  message?: string;
  http_status?: number;
  correlation_id?: string;
  client_id?: string;
  claim_ids?: string[];
}
interface AuraSubmitResponse {
  submitted_groups?: Array<{ aura_receivable_id?: number; client_id?: string; claim_ids?: string[] }>;
  failures?: AuraSubmitFailure[];
}

/**
 * Map an Aura failure to an actionable message (US #5332, AC2). Aura returns
 * {stage, code, message, http_status}; we prefix a user-actionable hint for the
 * known 4xx cases (partner mismatch, policy/claimant not found, validation) and
 * fall back to Aura's raw message.
 */
const describeAuraFailure = (f: AuraSubmitFailure): string => {
  // Before the keyword matches: this message names the claimant.
  if (f.code === "DEDUCTIBLE_REVIEW_PENDING")
    return "Held for deductible review — resolve the open deductible review before submitting (for an ASD claim, any review on its policy).";
  const raw = (f.message || f.code || "").trim();
  const hay = `${f.stage ?? ""} ${f.code ?? ""} ${f.message ?? ""}`.toLowerCase();
  if (hay.includes("partner")) return `Partner mismatch — this claim's partner isn't authorized in Aura. ${raw}`.trim();
  if (hay.includes("policy")) return `Policy not found in Aura — verify the policy is synced. ${raw}`.trim();
  if (hay.includes("claimant")) return `Claimant couldn't be resolved in Aura — verify the claimant. ${raw}`.trim();
  if (f.http_status === 422 || hay.includes("valid"))
    return `Aura rejected the submission: ${raw || "validation error"}.`;
  return raw || "request failed";
};

/**
 * Response of POST /stoploss-claims/mark-submitted (carrier track, US #5390).
 * actioned_count = claims newly stamped this call; skipped_count = claims in the
 * request not stamped (already carrier-submitted, not found, or wrong partner).
 * held_* = claims an open deductible review holds (#7340): not stamped and not in
 * skipped_count.
 */
interface MarkSubmittedToCarrierResponse {
  submitted_claim_ids?: string[];
  actioned_count?: number;
  skipped_count?: number;
  held_claim_ids?: string[];
  held_count?: number;
}

const summarizeFailures = (failures: AuraSubmitFailure[]): string =>
  failures.map(describeAuraFailure).filter(Boolean).join("; ") || "request failed";

/**
 * Shared hook for submitting stoploss claims.
 *
 * - "carrier": POSTs the batch to the carrier-submission endpoint
 *   (`/stoploss-claims/mark-submitted`, US #5390); stamps carrier_submitted_at
 *   and advances claim_status server-side, reporting actioned-vs-skipped.
 * - "aura": POSTs the batch to the Coronis→Aura funding-request sender
 *   (`/stoploss-claims/submit-to-aura`); Aura state (aura_receivable_id, etc.) is
 *   persisted server-side. Surfaces partial failures (US #5331).
 *
 * Used by StoplossClaimsShow.tsx and StoplossClaimsBulkActions.tsx.
 */
export const useSubmitStoplossClaims = () => {
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();

  const submitToCarrier = useCallback(
    async (claims: StoplossClaim[]) => {
      try {
        const { json } = await httpClient(`${getPartnerAwareApiUrl()}/${ResourceName.STOPLOSS_CLAIMS}/mark-submitted`, {
          method: "POST",
          body: JSON.stringify({ claim_ids: claims.map((c) => c.id) }),
        });
        const res = (json ?? {}) as MarkSubmittedToCarrierResponse;
        const actioned = res.actioned_count ?? 0;
        const skipped = res.skipped_count ?? 0;
        const held = res.held_count ?? res.held_claim_ids?.length ?? 0;
        refresh();

        // Claims an open deductible review holds were left unsubmitted (#7340). Say so,
        // and never read them as already submitted.
        const heldNote = held > 0 ? `${held} held for deductible review — resolve the review first` : "";
        if (held > 0) {
          if (actioned === 0) {
            notify(
              held === 1 && skipped === 0
                ? "Claim is held for deductible review — resolve the review before submitting it"
                : `No claims marked submitted to carrier: ${[heldNote, skipped > 0 ? `${skipped} not updated` : ""]
                    .filter(Boolean)
                    .join("; ")}`,
              { type: AlertType.WARNING }
            );
            return false;
          }
          notify(
            `Marked ${actioned} claim(s) submitted to carrier; ${[heldNote, skipped > 0 ? `${skipped} not updated` : ""]
              .filter(Boolean)
              .join("; ")}`,
            { type: AlertType.WARNING }
          );
          return true;
        }

        // Pure no-op (nothing newly stamped, only skips) — don't read as success.
        if (actioned === 0 && skipped > 0) {
          notify(
            skipped === 1
              ? "Claim was already submitted to carrier"
              : `All ${skipped} claim(s) were already submitted to carrier`,
            { type: AlertType.INFO }
          );
          return true;
        }

        const message =
          skipped > 0
            ? `Marked ${actioned} claim(s) submitted to carrier (${skipped} not updated)`
            : actioned === 1
              ? "Claim marked submitted to carrier"
              : `Marked ${actioned} claim(s) submitted to carrier`;
        notify(message, { type: AlertType.SUCCESS });
        return true;
      } catch (error) {
        notifyError(error);
        return false;
      }
    },
    [notify, notifyError, refresh]
  );

  const submitToAura = useCallback(
    async (claims: StoplossClaim[]) => {
      // AC1 — block and list missing required fields before the POST, so the user
      // gets a specific, correctable message instead of a generic Aura 4xx.
      const blockMessage = validateAuraSubmit(claims);
      if (blockMessage) {
        notify(blockMessage, { type: AlertType.ERROR });
        return false;
      }

      const claimIds = claims.map((claim) => claim.id);
      // AC3 — transient 5xx/timeout retry with backoff is handled server-side by the
      // sender (ADR-071); the message surfaced here is the post-retry outcome.
      try {
        const { json } = await httpClient(`${getPartnerAwareApiUrl()}/stoploss-claims/submit-to-aura`, {
          method: "POST",
          body: JSON.stringify({ claim_ids: claimIds }),
        });
        const res = (json ?? {}) as AuraSubmitResponse;
        const submitted = res.submitted_groups ?? [];
        const failures = res.failures ?? [];
        refresh();

        if (failures.length > 0) {
          // 200 with failures = partial success; report both sides.
          notify(
            `Submitted ${submitted.length} funding request(s) to Aura; ${failures.length} failed: ${summarizeFailures(failures)}`,
            { type: AlertType.WARNING }
          );
          return false;
        }
        if (submitted.length === 0) {
          // No-op 200 (shouldn't happen per the #5330 contract: nothing
          // submitted and nothing failed). Don't report it as success — a
          // silent no-op would read as a win. Surface it as neutral info.
          notify("No funding requests were submitted to Aura", { type: AlertType.INFO });
          return false;
        }
        notify(
          submitted.length <= 1
            ? "Claim(s) submitted to Aura"
            : `Submitted ${submitted.length} funding request(s) to Aura`,
          { type: AlertType.SUCCESS }
        );
        return true;
      } catch (error) {
        // 422 (all 4xx) / 502 (all 5xx) reject with the sender's `{ failures }` body,
        // which the real client keeps under `error.body.raw.body`. Reading `error.body`
        // found none, so every all-failed submit read "request failed", an all-held one
        // included (#7340).
        const body = apiErrorBody(error) as AuraSubmitResponse | undefined;
        const failures = Array.isArray(body?.failures) ? body.failures : [];
        notify(`Failed to submit to Aura: ${summarizeFailures(failures)}`, { type: AlertType.ERROR });
        refresh();
        return false;
      }
    },
    [notify, refresh]
  );

  const submit = useCallback(
    (claims: StoplossClaim[], target: SubmitTarget) =>
      target === "aura" ? submitToAura(claims) : submitToCarrier(claims),
    [submitToAura, submitToCarrier]
  );

  return { submit };
};
